import React, { Component } from 'react';
import { shape, object, array, string, number } from 'prop-types';
import { get } from 'lodash';
import BlockUser from '@fiverr-private/block_user/src/BlockUser';
import { api as collectApi } from '@fiverr-private/collect';
import Reviews from '@fiverr-private/reviews/src/reviews';
import { sessionStorage } from '@fiverr-private/futile';
import { FILTER_TYPES } from '../../../../server/utils/reviews';
import OutOfOffice from './OutOfOffice';
import InfoPanel from './InfoPanel';
import StickyProfile from './StickyProfile';
import CouponModal from './CouponModal';
import Gigs from './Gigs';
import {
    buildSellerCardData,
    buildPortfolioData,
    buildFiverrLearnData,
    buildProSubCategories
} from './lib/dataBuilder';
import reportEvent, { BQ_EVENT_TYPES } from './lib/reportEvent';
import { triggerMixPanelEvent, Events } from './lib/mixpanelEvents';
import { MODAL_WRAPPER_CLASS, REFERRERS } from './constants';

import './index.scss';

const { VIEWED_PAGE, INTERACTED, VIEWED_SELLER_PORTFOLIO, CLICKED_CONTACT_ME } = Events.GeneralEvents;
const showReviews = (reviewsObject) => (!!(reviewsObject && reviewsObject.reviews && reviewsObject.reviews.length));

export default class Index extends Component {

    constructor(props) {
        super(props);

        this.state = {
            collections: [],
            isCouponModalVisible: false
        };
    }

    componentDidMount() {
        const { requestContext, userData, gigs, gigIds, coupon, referrer } = this.props;
        const { current_user } = requestContext;

        const {
            id: sellerId,
            country,
            member_since,
            response_time,
            recent_delivery,
            is_seller
        } = userData.seller_card.user;

        const { courses } = userData;
        const userId = current_user && current_user.id || null;
        const type = is_seller ? 'seller_page.show' : 'users.show';
        const bqData = { userId, sellerId, gigIds };

        reportEvent(type, bqData);

        if (coupon && referrer) {
            const extra = {
                group: 'user_action',
                entity: 'client',
                event_source: 'seller_page',
                feature: {
                    name: 'seller_coupons'
                },
                action: {
                    type: 'click'
                },
                coupon: {
                    type: 'seller_coupon',
                    invitee_user_id: userId,
                    id: coupon.couponId,
                    promo_code: coupon.code,
                    invitation_id: coupon.deliveryPackId,
                    discount_percent: coupon.discountValue
                }
            };

            if (referrer === REFERRERS.EMAIL) {
                reportEvent(BQ_EVENT_TYPES.CLICKED_VIEW_COUPON_VIA_EMAIL, bqData, extra);
            }
            if (referrer === REFERRERS.NOTIFICATION) {
                reportEvent(BQ_EVENT_TYPES.CLICKED_VIEW_COUPON_VIA_NOTIFICATION, bqData, extra);
            }
        }

        const avarage_response_time = response_time > 0 ? { 'Avarage response time': response_time } : {};

        triggerMixPanelEvent(VIEWED_PAGE, userData, gigs.gigs, {
            'Seller Country': country,
            'Member since': new Date(member_since * 1000).getFullYear(),
            'Learn courses': courses.filter((course) => course.progression_status === 3).length,
            'Recent delivery': recent_delivery,
            ...avarage_response_time
        });

        // User-page-V2 hooks init
        global.fUserPagePerseus && global.fUserPagePerseus.init();

        this.fetchCollections();

        const couponId = get(coupon, 'couponId', '');
        const storageKey = this.buildDiscountStorageKey(couponId);
        if (!!coupon && !sessionStorage.get(storageKey)) {
            // eslint-disable-next-line react/no-did-mount-set-state
            this.setState({ isCouponModalVisible: true });
        }
    }

    buildDiscountStorageKey = (couponId) => `${couponId}_coupon_modal_viewed`

    handleFilterChange = (filterType) => {
        const { userData, gigs } = this.props;

        const mixPanelAction = ({
            'all': 'Filter most recent',
            'positive': 'Filter positive reviews',
            'negative': 'Filter negative reviews'
        })[filterType];

        triggerMixPanelEvent(INTERACTED, userData, gigs.gigs, { Action: mixPanelAction });
    }

    handlePortfolioClick = () => {
        const { userData, gigs } = this.props;
        triggerMixPanelEvent(VIEWED_SELLER_PORTFOLIO, userData, gigs.gigs, {});
    }

    handleContactClick = () => {
        const { requestContext } = this.props;
        const { current_user } = requestContext;
        const { userData, gigs, gigIds } = this.props;
        const { id: sellerId } = userData.seller_card.user;
        const userId = current_user.id;

        reportEvent('seller_page.contact_me', { userId, sellerId, gigIds });
        triggerMixPanelEvent(CLICKED_CONTACT_ME, userData, gigs.gigs, {});
    }

    scroll = (comp) => {
        this[comp].scrollIntoView({ block: 'start', behavior: 'smooth' });
    }

    closeCouponModal = () => {
        const { coupon } = this.props;

        const couponId = get(coupon, 'couponId', '');
        const storageKey = this.buildDiscountStorageKey(couponId);
        sessionStorage.set(storageKey, true);
        this.setState({ isCouponModalVisible: false });
    }

    async fetchCollections() {
        if (this.isLoggedIn()) {
            const { collections } = await collectApi.getCollections();
            this.setState({ collections });
        }
    }

    buildCollectProps() {
        const { collections } = this.state;

        return {
            collections,
            modalCustomName: MODAL_WRAPPER_CLASS,
            isLoggedIn: this.isLoggedIn(),
            onUpdateCollections: (collections) => {
                this.setState({ collections });
            }
        };
    }

    isLoggedIn() {
        const { requestContext } = this.props;

        return !!requestContext.current_user;
    }

    isPageOwner() {
        const { requestContext: { current_user }, userData: { seller_card: { user } } } = this.props;

        return this.isLoggedIn() && (user.username === current_user.username);
    }

    render() {
        const {
            requestContext,
            viewObject,
            gigs,
            userData,
            isTouch,
            coupon
        } = this.props;
        const { isCouponModalVisible } = this.state;
        const {
            page_ctx_id: pageCtxId, current_user = {},
            params = {}, ab_tests: abTests, locale, session_locale
        } = requestContext;

        const { user_id: sellerName } = params;
        const {
            currency,
            is_viewer_studio_lead: isViewerStudioLead
        } = viewObject;
        const currentUserId = current_user.id || null;
        const {
            user, courses, pro_badge, seller_card,
            seller_profile, buying_reviews, selling_reviews,
            interaction_block, portfolio_projects
        } = userData;
        const { id: sellerId } = seller_card.user;

        const collectProps = this.buildCollectProps();

        const sellerCardData = buildSellerCardData({
            requestContext,
            userData,
            currency,
            abTests,
            gigs,
            isViewerStudioLead,
            isTouch,
            collectProps
        });
        const portfolioProjects = buildPortfolioData(portfolio_projects, user);
        const fiverrLearnCourses = buildFiverrLearnData(courses);
        const proSubCategories = buildProSubCategories(pro_badge, locale || session_locale);
        const skillTests = seller_profile.passed_seller_tests;

        const showBuyingReviews = showReviews(buying_reviews);
        const showSellingReviews = showReviews(selling_reviews);
        const isBlocked = interaction_block && interaction_block.current_user_blocking;
        const {
            profile_photo: profilePhoto,
            has_live_gigs: hasLiveGigs,
            is_seller: isSeller,
            id: userNumberedId,
            is_pro: isPro
        } = sellerCardData.user;

        const showGigsSection = isSeller || hasLiveGigs;
        const showReviewsSection = showBuyingReviews || showSellingReviews;

        const reviewsBaseConfig = {
            pathFinderConfig: {
                path: 'fetch_user_reviews',
                params: {
                    perseus_name: 'user_page',
                    user_id: buying_reviews.user_id
                }
            },
            pageCtxId,
            pageName: 'user_page',
            biEventGroup: 'seller_page'
        };

        const buyingReviews = {
            ...reviewsBaseConfig,
            ...buying_reviews,
            type: FILTER_TYPES.relevant,
            displayRelevant: true,
            displayHelpful: true
        };

        const sellingReviews = { ...reviewsBaseConfig, ...selling_reviews };

        const scrollToData = [];

        if (showGigsSection) {
            scrollToData.push({ ref: 'gigRef', title: 'gigs', scrollFunc: this.scroll });
        }

        if (showReviewsSection) {
            scrollToData.push({ ref: 'reviewsRef', title: 'reviews', scrollFunc: this.scroll });
        }

        const { username, vacation_end_date, vacation_message, profile_photo, is_on_vacation: isOnVacation } = this.props.userData.seller_card.user;
        const outOfOfficeProps = {
            username,
            vacationEndDate: vacation_end_date,
            profilePhoto: profile_photo,
            isPageOwner: this.isPageOwner(),
            isOnVacation,
            vacationMessage: vacation_message,
            reason: ''
        };
        return (

        /* TODO remove the data attributes after refactoring user-page-perseus self-view script into a v4 package */
        /* The reason to the data attributes is that user-page-perseus script in v2 requires this data
            and the initialData is no longer available in v2 routes */
            <div className="user-page-perseus" data-username={sellerName}>

                <StickyProfile contactClick={this.handleContactClick}
                    sellerCard={sellerCardData}/>

                <div className="max-width-container">

                    <BlockUser isBlocked={isBlocked}
                        userId={userNumberedId}
                        username={sellerName}/>

                    <div className="grid-12">

                        <InfoPanel proSubCategories={proSubCategories}
                            currentUserNumberedId={currentUserId}
                            pageCtxId={pageCtxId}
                            sellerCard={sellerCardData}
                            scrollComponents={scrollToData}
                            portfolioProjects={portfolioProjects}
                            fiverrLearnCourses={fiverrLearnCourses}
                            sellerProfile={seller_profile}
                            skillTests={skillTests}
                            onPortfolioClick={(location) => this.handlePortfolioClick(location)}
                            locale={locale || session_locale}
                            coupon={coupon}
                            showGigsSection={showGigsSection}
                            showReviewsSection={showReviewsSection}/>

                        <div className="gigs-reviews-panel col-12-xs col-7-md col-8-lg" ref={(ref) => this.gigRef = ref}>
                            {isOnVacation &&
                                <OutOfOffice {...outOfOfficeProps}/>
                            }
                            {showGigsSection &&
                                <Gigs gigData={gigs}
                                    coupon={coupon}
                                    userId={currentUserId}
                                    sellerId={sellerId}
                                    sellerName={sellerName}
                                    pageCtxId={pageCtxId}
                                    currency={currency}
                                    isTouch={isTouch}
                                    abTestsProps={abTests}
                                    collectProps={collectProps}/>
                            }

                            {showReviewsSection &&
                                <div className="reviews-wrapper" ref={(ref) => this.reviewsRef = ref}>

                                    {showBuyingReviews && <Reviews initialState={buyingReviews}
                                        onFilterChange={(type) => this.handleFilterChange(type)}
                                        onLoadMoreClick={() => triggerMixPanelEvent(INTERACTED, userData, gigs.gigs, { Action: 'Load more' })}
                                        collapseOnMobile={false}
                                        isPro={isPro}/>}

                                    {showSellingReviews && <Reviews initialState={sellingReviews}
                                        onFilterChange={(type) => this.handleFilterChange(type)}
                                        onLoadMoreClick={() => triggerMixPanelEvent(INTERACTED, userData, gigs.gigs, { Action: 'Load more' })}
                                        collapseOnMobile={false}
                                        isPro={isPro}/>}
                                </div>
                            }

                        </div>
                    </div>
                </div>

                {coupon && isCouponModalVisible && (
                    <CouponModal {...coupon}
                        sellerId={sellerId}
                        currentUserId={currentUserId}
                        isVisible={isCouponModalVisible}
                        onClose={this.closeCouponModal}
                        sellerImage={profilePhoto}
                        sellerName={sellerName}/>
                )}

            </div>
        );
    }
}

Index.propTypes = {
    referrer: string,
    requestContext: shape({
        ab_tests: object
    }),
    viewObject: shape({
        currency: object
    }),
    gigs: shape({
        gigs: array
    }),
    userData: shape({
        user: object,
        seller_card: object,
        seller_profile: object,
        portfolio_projects: array,
        courses: array,
        pro_badge: object,
        buying_reviews: object,
        selling_reviews: object,
        interaction_block: object
    }),
    coupon: shape({
        couponId: string.isRequired,
        couponType: string.isRequired,
        deliveryPackId: string,
        code: string.isRequired,
        discountValue: number.isRequired,
        providerName: string.isRequired,
        expiresAt: string.isRequired,
        minOrderAmount: number.isRequired,
        note: string
    })
};

Index.defaultProps = {
    requestContext: {},
    viewData: {},
    viewObject: {
        currency: {
            name: 'USD',
            rate: '1',
            symbol: '$'
        }
    }
};
